<script lang="ts">
const POLLING_INTERVAL = 1;
</script>

<script setup lang="ts">
import { onMounted, onUnmounted, shallowRef } from 'vue';
import { storeToRefs } from 'pinia';
import MarketplaceTopOfBookWatchlistTabs from '@/modules/marketplace/components/MarketplaceTopOfBookWatchlistTabs.vue';
import MarketplaceTopOfBookTable from '@/modules/marketplace/components/MarketplaceTopOfBookTable.vue';
import { VenueTopOfBookResponse } from '@/modules/marketplace/models';
import axios from 'axios';
import { fileNameFromHeaders } from '@/utils/api/helpers';
import { errorString } from '@/utils/helpers/rest-response';
import { rateTypeOptions } from '@/modules/marketplace/helpers/marketplace';
import { useRateTypeStore } from '@/store/rate-type-store';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useRouter } from '@/router/router';

const props = withDefaults(defineProps<{ withWatchlists?: boolean; asBroker?: boolean }>(), {
  withWatchlists: false,
  asBroker: false,
});

const marketplaceApiService = useMarketplaceApiService();
const snackbar = useSnackbarManager();
const router = useRouter();

const { marketplaceRateType } = storeToRefs(useRateTypeStore());

const topOfBook = shallowRef<VenueTopOfBookResponse['data']>([]);
const watchlistDisplayId = shallowRef<string | null>(null);
const pollInterval = shallowRef<ReturnType<typeof setInterval> | null>(null);

async function fetchContents(): Promise<void> {
  if (props.asBroker) {
    topOfBook.value = await marketplaceApiService.fetchAdminTopOfBook();
  } else {
    topOfBook.value =
      watchlistDisplayId.value === null
        ? await marketplaceApiService.fetchTopOfBook()
        : await marketplaceApiService.fetchWatchlistContent(watchlistDisplayId.value);
  }
}

function onTabChange(displayId: string | null): void {
  watchlistDisplayId.value = displayId;
  void fetchContents();
}

async function exportAsCsv(excel = false): Promise<void> {
  try {
    const response = await axios.get<BlobPart>('/api/1/orderbook/top-of-book/export/csv', {
      params: {
        excel: excel,
      },
      responseType: 'blob',
    });

    const fileName =
      fileNameFromHeaders(response.headers as Record<string, string>) || 'export.csv';
    const blob = new Blob([response.data], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

async function exportAsExcel(): Promise<void> {
  return exportAsCsv(true);
}

function handleRowClick(cusip: string): void {
  if (props.asBroker) {
    void router.push({
      name: 'broker-user.client-monitor.marketplace-depth-of-book',
      params: { cusip },
    });
  } else {
    void router.push({
      name: 'marketplace.symbol-overview',
      params: { cusip },
    });
  }
}

onMounted(async () => {
  await fetchContents();
  pollInterval.value = setInterval(fetchContents, POLLING_INTERVAL * 1000);
});

onUnmounted(() => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
  }
});

defineExpose({
  test: {
    onItemSelected: handleRowClick,
  },
});
</script>

<template>
  <span style="display: contents">
    <div class="d-flex justify-space-between">
      <MarketplaceTopOfBookWatchlistTabs
        v-if="withWatchlists"
        :selected-display-id="watchlistDisplayId"
        @change="onTabChange($event)"
      >
        <template #additional-buttons>
          <AuroraBtnDropdown color="secondary" main-text="Export" @click="exportAsCsv">
            <VList dense>
              <VListItem @click="exportAsCsv">
                <VListItemContent>
                  <VListItemTitle>CSV</VListItemTitle>
                </VListItemContent>
                <VListItemAction>
                  <VIcon dense small>mdi-file-download</VIcon>
                </VListItemAction>
              </VListItem>
              <VListItem @click="exportAsExcel">
                <VListItemContent>
                  <VListItemTitle>Excel</VListItemTitle>
                </VListItemContent>
                <VListItemAction>
                  <v-icon dense small>mdi-file-excel</v-icon>
                </VListItemAction>
              </VListItem>
            </VList>
          </AuroraBtnDropdown>
        </template>
      </MarketplaceTopOfBookWatchlistTabs>
      <VSelect
        v-model="marketplaceRateType"
        class="ma-2 flex-grow-0 flex-basis-25"
        :items="rateTypeOptions"
        label="Rate Type"
      />
    </div>
    <MarketplaceTopOfBookTable :items="topOfBook" @click:row="handleRowClick" />
  </span>
</template>

<style lang="scss" scoped></style>
